import { ErrorBoundary, Shadow } from '@genial/components';
import { BoxBorder } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import { Box, Center, FlatList } from 'native-base';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { CustomerDetailStackParamList } from '../../../../../../../routes/types';
import { movementOptionQueryState } from '../../../../../../../store/customerDetail/movement/atom';
import { movementStatementsQuery } from '../../../../../../../store/customerDetail/movement/selectors';
import { FinancialMovementsResponseNormalized } from '../../../../../../../store/customerDetail/movement/types';
import { Item as MovementItem } from '../Item';
import { Skeleton } from '../Skeleton';

interface ListProps {}

export const List: React.FC<ListProps> = () => {
  const { params } = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const currentOptionTypeSelected = useRecoilValue(movementOptionQueryState);
  const accountNumber = params?.accountNumber;
  const [_, setCurrentOption] = useState(currentOptionTypeSelected);
  const [allData, setAllData] = useState<FinancialMovementsResponseNormalized[]>([]);

  const viewportType = useBreakpointValue({ sm: 'tablet', lg: 'desktop' });

  const {
    data: response,
    error,
    loading,
    refresher,
  } = useRecoilQuery(movementStatementsQuery({ accountNumber }), { cachePolicy: 'no-cache' });

  useEffect(() => {
    setCurrentOption(currentOptionTypeSelected);
    setAllData([]);
  }, [currentOptionTypeSelected]);

  useEffect(() => {
    const items = response?.data;

    if (!loading && items?.length) {
      setAllData(items);
    }
  }, [loading, response, setAllData]);

  const loadingFooter = () => {
    return (
      <Box>
        {!!loading && (
          <Shadow borderRadius={BoxBorder.heavy} level={3}>
            <Skeleton viewportType={viewportType} renderCount={8} />
          </Shadow>
        )}
      </Box>
    );
  };

  if (error) {
    return (
      <Center h={'400px'}>
        <ErrorBoundary onRefresh={refresher} />
      </Center>
    );
  }

  if (!loading && response?.data && response?.data.length === 0) {
    return (
      <Center h={'400px'}>
        <ErrorBoundary
          errorIconOrIllustration="NotFoundIllustration"
          errorTitle="Nenhum resultado corresponde à sua pesquisa"
          errorMessage="Por favor, verifique o campo informado e tente novamente."
        />
      </Center>
    );
  }

  return (
    <Shadow level={3}>
      <FlatList
        maxH="800px"
        data={allData}
        ListFooterComponent={loadingFooter}
        onEndReachedThreshold={1.5}
        windowSize={10}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={5}
        showsVerticalScrollIndicator={false}
        keyExtractor={(keyItem, keyIndex) => `movement-${keyItem.movementId}-${keyIndex}`}
        renderItem={({ item }) => <MovementItem {...item} />}
      />
    </Shadow>
  );
};
